import {IServiceFactory} from "./service-factory.interface";
import {Lazy} from "../utils/lazy";
import {IStationService} from "./stations/station.service.interface";
import {StationService} from "./stations/station.service";
import {IFlightSearchService} from "./flight-search/flight-search.service.interface";
import {FlightSearchService} from "./flight-search/flight-search.service";
import {ILanguageService} from "./language/language.service.interface";
import {LanguageService} from "./language/language.service";
import {IDotRezApiService} from "./dot-rez-api/dot-rez-api.service.interface";
import {DotRezApiService} from "./dot-rez-api/dot-rez-api.service";
import {ILowFareService} from "./low-fare/low-fare.service.interface";
import {LowFareService} from "./low-fare/low-fare.service";
import {ICurrencyService} from "./currency/currency.service.interface";
import {CurrencyService} from "./currency/currency.service";
import {IFlightsScheduleService} from "./flights-schedule/flights-schedule.service.interface";
import {FlightsScheduleService} from "./flights-schedule/flights-schedule.service";
import {LoadingIndicatorService} from "./loading-indicator/loading-indicator.service";
import {ILoadingIndicatorService} from "./loading-indicator/loading-indicator.service.interface";
import {ITimeService} from "./time/time.service.interface";
import {TimeService} from "./time/time.service";
import {IThemeService} from "./theme/theme.service.interface";
import {ThemeService} from "./theme/theme.service";
import {ILoggerService} from "./logger/logger.service.interface";
import {ConsoleLoggerService} from "./logger/console-logger.service";
import {IPassengersTypesService} from "./passenger-types/passengers-types.service.interface";
import {PassengersTypesService} from "./passenger-types/passengers-types.service";
import {IDocumentService} from "./document/document.service.interface";
import {DocumentService} from "./document/document.service";
import {IWindowService} from "./window/window.service.interface";
import {WindowService} from "./window/window.service";
import {ConfigurationService} from "./configuration/configuration.service";
import {ICountryService} from "./country/country.service.interface";
import {CountryService} from "./country/country.service";
import {IAirlineWebapiService} from "./airline-webapi/airline-webapi.service.interface";
import {AirlineWebapiService} from "./airline-webapi/airline-webapi.service";
import {makeObservable, observable, runInAction} from "mobx";
import {IApplicationService} from "./application/application.service.interface";
import {ApplicationService} from "./application/application.service";
import {IUserService} from "./user/user.service.interface";
import {UserService} from "./user/user.service";
import {IAlertService} from "./alert/alert.service.interface";
import {AlertService} from "./alert/alert.service";
import {IMediaChecks} from "./media-query/media-queries.interface";
import {ICmsService} from "./cms/cms.service.interface";
import {CmsService} from "./cms/cms.service";
import {ICryptoService} from "./crypto/crypto.service.interface";
import {CryptoService} from "./crypto/crypto.service";
import {ScreenMediaQueryChecks} from "./media-query/screen/screen-media-query-checks";
import {IDialogService} from "./dialog/dialog.service.interface";
import {DialogService} from "./dialog/dialog.service";
import {ILargeStorageConsumersManagerService} from "./storage/large-storage-consumers-manager.service.interface";
import {LargeStorageConsumersManagerService} from "./storage/large-storage-consumers-manager.service";
import {IRawSessionStorageService} from "./storage/raw-session-storage/raw-session-storage.service.interface";
import {RawSessionStorageService} from "./storage/raw-session-storage/raw-session-storage.service";
import {IRawLocalStorageService} from "./storage/raw-local-storage/raw-local-storage.service.interface";
import {RawLocalStorageService} from "./storage/raw-local-storage/raw-local-storage.service";

export class ServiceFactory implements IServiceFactory {
    constructor(/*history: HistoryType*/) {

        makeObservable(this, {
            isLoading: observable.ref
        });

        this.configuration = new ConfigurationService(this);

        this.configuration.loadConfigurations().then(() => {
            /*delay(Date.now(), 5000).then(() => {
            })
             */

            this._country.forceInit();
            this._stations.forceInit();
            this._flightSearch.forceInit();
            this._onLoadingFinish();

        }).catch(() => {
            this._onLoadingFinish();
        });
    }

    isLoading: boolean = true;
    private _onLoadingFinish(): void {
        runInAction(() => {
            this.isLoading = false;
        });
    }

    readonly configuration: ConfigurationService;
    readonly logger: ILoggerService = new ConsoleLoggerService();

    private _application: Lazy<IApplicationService> = new Lazy<IApplicationService>(() => new ApplicationService(this));
    get application(): IApplicationService {
        return this._application.value;
    }

    private _dotRezApi: Lazy<IDotRezApiService> = new Lazy<IDotRezApiService>(() => new DotRezApiService(this));
    get dotRezApi(): IDotRezApiService {
        return this._dotRezApi.value;
    }

    private _stations: Lazy<IStationService> = new Lazy<IStationService>(() => new StationService(this));
    get stations(): IStationService {
        return this._stations.value;
    }

    private _flightSearch: Lazy<IFlightSearchService> = new Lazy<IFlightSearchService>(() => new FlightSearchService(this));
    get flightSearch(): IFlightSearchService {
        return this._flightSearch.value;
    }

    private _language: Lazy<ILanguageService> = new Lazy<ILanguageService>(() => new LanguageService(this));
    get language(): ILanguageService {
        return this._language.value;
    }
    private _lowFare: Lazy<ILowFareService> = new Lazy<ILowFareService>(() => new LowFareService(this));
    get lowFare(): ILowFareService {
        return this._lowFare.value;
    }

    private _currency: Lazy<ICurrencyService> = new Lazy<ICurrencyService>(() => new CurrencyService(this));
    get currency(): ICurrencyService {
        return this._currency.value;
    }

    private _flightsSchedule: Lazy<IFlightsScheduleService> = new Lazy<IFlightsScheduleService>(() => new FlightsScheduleService(this));
    get flightsSchedule(): IFlightsScheduleService {
        return this._flightsSchedule.value;
    }

    private _loadingIndicator: Lazy<ILoadingIndicatorService> = new Lazy<ILoadingIndicatorService>(() => new LoadingIndicatorService(this));
    get loadingIndicator(): ILoadingIndicatorService {
        return this._loadingIndicator.value;
    }

    private _time: Lazy<ITimeService> = new Lazy<ITimeService>(() => new TimeService(this));
    get time(): ITimeService {
        return this._time.value;
    }

    private _theme: Lazy<IThemeService> = new Lazy<IThemeService>(() => new ThemeService(this));
    get theme(): IThemeService {
        return this._theme.value;
    }

    private _passengerTypes: Lazy<IPassengersTypesService> = new Lazy<IPassengersTypesService>(() => new PassengersTypesService(this));
    get passengerTypes(): IPassengersTypesService {
        return this._passengerTypes.value;
    }

    private _rawSessionStorage: Lazy<IRawSessionStorageService> = new Lazy<IRawSessionStorageService>(() => new RawSessionStorageService(this));
    get rawSessionStorage(): IRawSessionStorageService {
        return this._rawSessionStorage.value;
    }

    private _rawLocalStorage: Lazy<IRawLocalStorageService> = new Lazy<IRawLocalStorageService>(() => new RawLocalStorageService(this));
    get rawLocalStorage(): IRawLocalStorageService {
        return this._rawLocalStorage.value;
    }

    private _largeStorageConsumersManager: Lazy<ILargeStorageConsumersManagerService> = new Lazy<ILargeStorageConsumersManagerService>(() => new LargeStorageConsumersManagerService(this));

    get largeStorageConsumersManager(): ILargeStorageConsumersManagerService {
        return this._largeStorageConsumersManager.value;
    }


    private _document: Lazy<IDocumentService> = new Lazy<IDocumentService>(() => new DocumentService());
    get document(): IDocumentService {
        return this._document.value;
    }

    private _window: Lazy<IWindowService> = new Lazy<IWindowService>(() => new WindowService());
    get window(): IWindowService {
        return this._window.value;
    }


    private _country: Lazy<ICountryService> = new Lazy<ICountryService>(() => new CountryService(this));
    get country(): ICountryService {
        return this._country.value;
    }

    private _airlineWebapi: Lazy<IAirlineWebapiService> = new Lazy<IAirlineWebapiService>(() => new AirlineWebapiService(this));
    get airlineWebapi(): IAirlineWebapiService {
        return this._airlineWebapi.value;
    }

    private _user: Lazy<IUserService> = new Lazy<IUserService>(() => new UserService(this));
    get user(): IUserService {
        return this._user.value;
    }

    private _alert: Lazy<IAlertService> = new Lazy<IAlertService>(() => new AlertService(this));
    get alert(): IAlertService {
        return this._alert.value;
    }

    private _screenMediaQuery: Lazy<IMediaChecks> = new Lazy<ScreenMediaQueryChecks>(() => new ScreenMediaQueryChecks());
    get screenMediaQuery(): IMediaChecks {
        return this._screenMediaQuery.value;
    }

    private _cms: Lazy<ICmsService> = new Lazy<ICmsService>(() => new CmsService(this));
    get cms(): ICmsService {
        return this._cms.value;
    }

    private _crypto: Lazy<ICryptoService> = new Lazy<ICryptoService>(() => new CryptoService());
    get crypto(): ICryptoService {
        return this._crypto.value;
    }

    private _dialog: Lazy<IDialogService> = new Lazy<IDialogService>(() => new DialogService(this));
    get dialog(): IDialogService {
        return this._dialog.value;
    }

}
